/**
 * THE STAMP REGISTRY: which types exist, what each one is at a given LOD, and the cache that keeps
 * from building it twice.
 *
 * ONE FILE, because the two halves this merges were never independent. `lodSpecs` held the map of
 * types to specs and `StampDefinitions` held the generator that consulted it — so every question
 * ("what does a lod-3 tower look like?", "how wide is its footprint?", "is there a model at all up
 * there?") already needed both, they each carried their own copy of VARIANTS_PER_TYPE, and the import
 * only ran one way because the spec modules were carefully kept from naming the registry.
 *
 * WHAT LIVES HERE is registry policy: the type -> spec map, the LOD-0/fold/stand-in dispatch, the yaw
 * quantization and shape-class keying that make coarse instances shareable, the LRU, and the footprint
 * radius used to cull a placement without generating it. What does NOT live here: the shapes
 * themselves (types/*.ts), the coarse LOD policy they draw with (coarseModel.ts), and the lattice
 * operations underneath both (voxel/blobs, voxel/fold, voxel/drawing).
 */
import { partExtent, rasterizePartsAtScale } from '../../voxel/drawing.js';
import { foldVoxels } from '../../voxel/fold.js';
import { StampType, calculateBounds } from './StampTypes.js';
import { MAX_SCATTER_LOD } from './lodLevels.js';
import { TREE_OAK_SPEC } from './types/treeOak.js';
import { TREE_OAK_LARGE_SPEC } from './types/treeOakLarge.js';
import { TREE_PINE_SPEC } from './types/treePine.js';
import { BUILDING_SMALL_SPEC } from './types/buildingSmall.js';
import { BUILDING_HUT_SPEC } from './types/buildingHut.js';
import { BUILDING_TOWER_SPEC } from './types/buildingTower.js';
import { ROCK_SMALL_SPEC, ROCK_MEDIUM_SPEC, ROCK_LARGE_SPEC } from './types/rock.js';
import { TORCH_SPEC } from './types/torch.js';
export { StampType, calculateBounds };
export { MAX_SCATTER_LOD };
// ============== The Types ==============
/* eslint-disable @typescript-eslint/no-explicit-any */
const SPECS = new Map([
    [StampType.TREE_OAK, TREE_OAK_SPEC],
    [StampType.TREE_OAK_LARGE, TREE_OAK_LARGE_SPEC],
    [StampType.TREE_PINE, TREE_PINE_SPEC],
    [StampType.BUILDING_SMALL, BUILDING_SMALL_SPEC],
    [StampType.BUILDING_HUT, BUILDING_HUT_SPEC],
    [StampType.BUILDING_TOWER, BUILDING_TOWER_SPEC],
    [StampType.ROCK_SMALL, ROCK_SMALL_SPEC],
    [StampType.ROCK_MEDIUM, ROCK_MEDIUM_SPEC],
    [StampType.ROCK_LARGE, ROCK_LARGE_SPEC],
    [StampType.TORCH, TORCH_SPEC],
]);
/** Variants per type — the scatter picks one of four (StampPointGenerator). */
const VARIANTS_PER_TYPE = 4;
export function stampSpec(type) {
    return SPECS.get(type);
}
/**
 * Coarsest LOD at which ANY stamp still produces per-stamp geometry. The coarse generator skips the
 * whole scatter+apply path above this.
 */
export function maxStampLod() {
    let m = 0;
    for (const s of SPECS.values())
        m = Math.max(m, s.maxLod);
    return m;
}
/**
 * The per-column dot used above MAX_SCATTER_LOD: the tallest crown and the leaf material over every
 * spec that is still drawn up there, so the speckle does not shrink or change colour crossing that
 * boundary.
 *
 * ONLY SPECS THAT OUTLIVE THE CUTOFF FEED IT. The dot is the answer to "the scatter has saturated,
 * so every eligible column gets one" — true of trees at one per 5 m, false of buildings at one per
 * 50-70 m. A building's stand-in is both taller and a different colour, so including it would paint
 * a roof-coloured speckle 10 m tall on EVERY column of the far rings. Buildings stop at
 * maxLod = MAX_SCATTER_LOD instead, which is exactly the statement "no picture of me above the
 * cutoff", and this filter is that statement being honoured rather than a second list to maintain.
 */
export function columnDot() {
    let topY = 0;
    let material = -1;
    for (const s of SPECS.values()) {
        if (s.maxLod <= MAX_SCATTER_LOD)
            continue;
        for (let variant = 0; variant < VARIANTS_PER_TYPE; variant++) {
            const p = s.params(variant, 0);
            for (const part of s.lodParts?.(p, MAX_SCATTER_LOD) ?? []) {
                const t = part.offset.y + part.config.size.y;
                if (t > topY) {
                    topY = t;
                    material = part.config.material;
                }
            }
        }
    }
    return material < 0 ? null : { material, topY };
}
/**
 * Whether a stamp type is a building. Buildings share behaviour across the whole pipeline: they are
 * the only ROTATABLE stamps (so their yaw and per-instance seed enter the cache key), they are applied
 * last so their interior carves win (StampPlacer), they are SEATED by rounding rather than flooring
 * (stampSeatRow), and they respond to the building-spacing control (TerrainGenerator).
 *
 * `isRotatableStamp` used to be a second name for this exact predicate. One question, one answer.
 */
export function isBuildingStamp(type) {
    return type === StampType.BUILDING_SMALL ||
        type === StampType.BUILDING_HUT ||
        type === StampType.BUILDING_TOWER;
}
// ============== The Cache ==============
/**
 * How many yaw steps a stamp is snapped to at a given level — 0 meaning "yaw has nothing to turn".
 *
 * A coarse building is cached by its key, and every scattered building has its own rotation — so
 * without snapping, a coarse ring holds one entry per INSTANCE and reuses nothing. The step is sized
 * against what the lattice can express at that stride: 11.25° at lod 1 (a 12 m house is 24 voxels
 * across there, and the snap is under half a voxel at its corner), 22.5° at lod 2-3, and none at
 * lod 4+, where the model is a block one to three voxels wide.
 *
 * LOD 0 IS NEVER SNAPPED — the LOD-0 building checksums depend on the exact yaw, and the near view is
 * where a snap would be seen. Nor are the FOLD levels: there the model IS the LOD-0 model.
 */
function yawSteps(lod) {
    return lod === 1 ? 32 : lod <= 3 ? 16 : 0;
}
/**
 * The cache key. AN INTEGER WHENEVER THE IDENTITY IS INTEGRAL, which is almost always.
 *
 * A stamp's identity is (type, variant, yaw, seed, lod), and four of those five are small integers.
 * The fifth is a float only for a building drawn from its real model — LOD 0 and the fold levels —
 * because there the exact placement yaw reaches the SDF evaluator and the whole 32-bit seed picks the
 * decorations. Everywhere else the yaw is a snap STEP (yawSteps) and the seed is a small shape class
 * (StampSpec.coarseSeedClass), so the key packs into 23 bits and costs no allocation:
 *
 *   type 4 | variant 2 | lod 4 | yaw step 6 | seed class 8
 *
 * That covers every tree, rock and torch at every level and every coarse building above the fold —
 * measured, 7148 of the 7148 getStamp calls made generating four coarse rings. The float cases fall
 * back to a string, and a Map holds both kinds of key without them ever colliding. Worth doing: at
 * 350 ns each the string keys were 2.5 ms per 7148 lookups against 0.31 ms packed, and the number that
 * makes it worth doing at all is that these are CACHE HITS — the key is the whole cost.
 */
const TYPE_INDEX = new Map(getAllStampTypes().map((t, i) => [t, i]));
export const MAX_KEY_LOD = 15;
export const MAX_YAW_STEP = 63;
export const MAX_SEED_CLASS = 255;
export function packStampKey(type, variant, lod, yawStep, seedClass) {
    const ti = TYPE_INDEX.get(type) ?? 0;
    return ((ti << 20) | (variant << 18) | (lod << 14) | (yawStep << 8) | seedClass) >>> 0;
}
/**
 * Get a stamp definition by type, variant, rotation, seed and LOD.
 *
 * @param type - Stamp type
 * @param variant - Variant index (0-3)
 * @param rotation - Rotation in radians around Y axis (only used for buildings)
 * @param seed - Per-instance seed (buildings: decorations, and the tower's floor count)
 * @param lod - 0 for the full model, >= 1 for the coarse stand-in on that level's lattice
 */
export function getStamp(type, variant, rotation = 0, seed = 0, lod = 0) {
    const spec = stampSpec(type);
    const normalizedVariant = variant % (spec?.variants ?? VARIANTS_PER_TYPE);
    // Rotation + seed only affect buildings (rotatable, ~95k-sample SDF); include them in the key so
    // building instances are cached too. Previously buildings were regenerated on EVERY call — and
    // stampAffectsChunk regenerates one just to read its bounds — so a nearby tower was rebuilt ~2× per
    // chunk, per cy in the column, per neighbour in the scatter scan. The result is a pure function of
    // (type,variant,rotation,seed), so caching is byte-identical (callers treat the stamp read-only).
    const building = isBuildingStamp(type);
    // A FOLD level draws the LOD-0 model itself (see voxel/fold), so it is keyed like LOD 0: exact
    // rotation, exact seed. That costs one cache entry per building instance and buys two things — no
    // orientation snap in the near ring, where a building is largest on screen, and no question of
    // whether a shape class is honest about a model that includes every decoration down to the noise in
    // a ceiling blob.
    const folded = lod > 0 && lod <= (spec?.foldLod ?? 0);
    // The two cases where the identity carries a float and a full seed, i.e. where the key is a string.
    const exact = building && (lod === 0 || folded);
    const steps = building && !exact ? yawSteps(lod) : 0;
    // Kept as the STEP INDEX as well as the angle: the index is what the packed key holds. Index 32 is
    // a distinct key from 0 on purpose — quantizeYaw always let a yaw just under a full turn land there,
    // and 32*step is a hair off 0 in floating point, so a shared entry would not be byte-identical.
    const yawStep = steps === 0 ? 0 : Math.round(rotation / ((Math.PI * 2) / steps));
    const rot = exact ? rotation : (steps === 0 ? 0 : yawStep * ((Math.PI * 2) / steps));
    const sd = building ? seed : 0;
    // Above the fold levels a coarse model is keyed by the seed's SHAPE CLASS rather than the seed: the
    // decorations are gone and only dimensions remain, so every seed that yields the same dimensions
    // yields the same stand-in — the tower's 2-4 floors are three classes, the house and hut one each.
    // Keying on the raw seed there would give every scattered instance its own entry and reuse nothing.
    const seedClass = exact ? sd : (spec?.coarseSeedClass?.(sd) ?? 0);
    // The lod is part of the identity: the same tree is a different MODEL at each level, and coarse
    // models live on a coarse lattice, so a lod-less key would hand a lod-2 caller a lod-0 stamp.
    // A spec is free to return any integer from coarseSeedClass, and a level counter is free to grow, so
    // anything that would not FIT its field takes the string key. Overflowing a field silently aliases
    // one stamp onto another, which is the one failure mode a cache must not have.
    const packable = !exact && lod <= MAX_KEY_LOD
        && yawStep >= 0 && yawStep <= MAX_YAW_STEP
        && seedClass >= 0 && seedClass <= MAX_SEED_CLASS;
    const key = packable
        ? packStampKey(type, normalizedVariant, lod, yawStep, seedClass)
        : `${type}:${normalizedVariant}:${rot}:${seedClass}:${lod}`;
    if (stampCache.has(key)) {
        const hit = stampCache.get(key) ?? null;
        stampCache.delete(key); // re-insert → Map iteration order is the LRU order evict() uses
        stampCache.set(key, hit);
        return hit;
    }
    const stamp = createStamp(type, normalizedVariant, rot, sd, lod);
    // Nulls are cached too: "this type has no model at this level" is the common case above maxLod and
    // is worth answering without re-consulting the registry per placement per chunk.
    stampCache.set(key, stamp);
    stampCacheVoxels += stamp?.voxels.length ?? 0;
    evictStamps();
    return stamp;
}
/** Cache for all stamps (trees/rocks/torch by type:variant; buildings also by rotation:seed). */
const stampCache = new Map();
const STAMP_CACHE_MAX = 512;
/**
 * ...and a bound on the VOXELS those entries hold, which is the resource that actually runs out.
 *
 * A coarse stand-in is solid, so its voxel count goes with its volume: a lod-1 oak is ~1k voxels but
 * a lod-1 house is ~8k and gets its own entry per (variant, yaw step, level). 512 such entries is
 * hundreds of MB — the entry count alone stopped describing the cost the moment buildings joined.
 *
 * Eviction is LRU (Map iteration order, refreshed on every hit) rather than the clear-all this
 * replaced: a clear triggered by one ring of houses also threw away the tree stand-ins, which are hit
 * on every column of every chunk.
 */
/*
 * RAISED 400_000 -> 1_000_000 FOR THE BIG OAK. Its LOD-0 model is ~109k voxels and its lod-1 fold
 * ~12k, against ~1k for a small tree's stand-in; four variants of it plus their folds is ~0.48M,
 * which would have been MORE THAN the old bound on its own — a single ring of them evicted every
 * tree, rock and building stand-in in the cache, the exact thrash the LRU replaced a clear-all to
 * avoid. A VoxelSample is five numbers in an object, ~70 B on V8, so the bound is ~70 MB in the worst
 * case and far less in practice: it is only reached by entries actually asked for.
 *
 * (The figures were ~54k/~6k when this was raised, for a shell-of-clusters oak with twelve lengths of
 * wood in it. The 2026-08-28 model rewrite — envelope-first foliage, space-colonised skeleton — is
 * twice the model at the same 16 m footprint, which is what the halved headroom above is. The next
 * shape that wants four distinct skeletons is the one that has to move this number again.)
 */
const STAMP_CACHE_MAX_VOXELS = 1_000_000;
let stampCacheVoxels = 0;
/**
 * Drop every cached model and footprint. For a change to something a model reads OUTSIDE its cache
 * key — today the big oak's world-config shape (treeOakLarge.setOakLargeShape) — since the key is
 * (type, variant, lod, yaw, seed) and knows nothing else. Cheap and rare: a config change.
 */
export function invalidateStampCache() {
    stampCache.clear();
    stampCacheVoxels = 0;
    footprintRadiusCache.clear();
}
function evictStamps() {
    while (stampCache.size > STAMP_CACHE_MAX || stampCacheVoxels > STAMP_CACHE_MAX_VOXELS) {
        const oldest = stampCache.keys().next();
        if (oldest.done)
            return;
        stampCacheVoxels -= stampCache.get(oldest.value)?.voxels.length ?? 0;
        stampCache.delete(oldest.value);
    }
}
/** Per-(type, variant, lod) rotation-invariant horizontal footprint radius (voxels), for cheap cull. */
const footprintRadiusCache = new Map();
/**
 * Rotation-invariant horizontal footprint radius, in the STAMP's own voxels (coarse ones at lod >= 1):
 * the max distance from the stamp origin to any of its voxels in X/Z.
 *
 * Used by stampAffectsChunk and the coarse placement filter to cull WITHOUT generating a building's
 * ~95k-sample rotated SDF per candidate (the dominant cost of exploring into new columns). A yaw is a
 * rotation about the stamp origin, so it preserves every voxel's distance from it — one measurement
 * therefore bounds every instance, and the cull can never drop an overlapping stamp. Cached per
 * type:variant:lod. The two levels measure it differently; see each helper for why.
 */
export function getStampFootprintRadius(type, variant, lod = 0) {
    // Per-lod: a stand-in is not always narrower than its LOD-0 source (the lod-1 tree trunk is
    // deliberately FATTER, since the real radius is sub-voxel at that stride). A lod-less radius would
    // under-cull the wider model and drop it at chunk borders, popping it in and out.
    const key = packStampKey(type, variant % VARIANTS_PER_TYPE, lod, 0, 0);
    let r = footprintRadiusCache.get(key);
    if (r === undefined) {
        r = lod === 0 ? lod0FootprintRadius(type, variant) : coarseFootprintRadius(type, variant, lod);
        footprintRadiusCache.set(key, r);
    }
    return r;
}
/** The LOD-0 radius: measured from the model, whose voxel lattice resolves the shape it samples. */
function lod0FootprintRadius(type, variant) {
    const b = getStamp(type, variant, 0, 0, 0)?.bounds
        ?? { minX: 0, maxX: 0, minY: 0, maxY: 0, minZ: 0, maxZ: 0 };
    const hx = Math.max(Math.abs(b.minX), Math.abs(b.maxX));
    const hz = Math.max(Math.abs(b.minZ), Math.abs(b.maxZ));
    return Math.sqrt(hx * hx + hz * hz); // circumscribed radius — bounds the footprint at any rotation
}
/**
 * The COARSE radius, measured from the PARTS rather than from the voxels they rasterize to.
 *
 * Measuring the voxels is what LOD 0 does, and it is wrong here for one reason: a coarse voxel is
 * emitted only where the SDF is STRICTLY inside, so a box whose face lands exactly on the lattice
 * loses that row and reads NARROWER than the shape. A lod-3 house is 3 coarse voxels of half-width
 * and emits 2, giving a diagonal of 2.83 — while the same house turned 45° reaches 4.24, because its
 * corner is a real corner. The cull then drops a rotated building at a chunk border and it pops in and
 * out as you walk (caught by lodStandIn's per-voxel radius case). The shape's own diagonal is
 * rotation-invariant and bounds every instance.
 */
function coarseFootprintRadius(type, variant, lod) {
    const spec = stampSpec(type);
    if (!spec)
        return 0; // no coarse model at all
    // A fold IS the LOD-0 model at 1/stride scale, so its radius is LOD 0's at that scale — measured
    // from voxels, like LOD 0's, because the truncation this function exists to avoid is a property of
    // rasterizing a SHAPE onto a coarse lattice, not of folding a model that was already voxels.
    if (lod <= (spec.foldLod ?? 0))
        return lod0FootprintRadius(type, variant) / (1 << lod);
    const parts = lod > spec.maxLod ? null : spec.lodParts?.(spec.params(variant, 0), lod);
    let r = 0;
    for (const part of parts ?? []) {
        const e = partExtent(part.config);
        // Conservative in the part's own yaw too (a gable half is mirrored about the ridge).
        r = Math.max(r, Math.hypot(Math.abs(part.offset.x) + e.x, Math.abs(part.offset.z) + e.z));
    }
    return r / (1 << lod); // callers work in the stamp's own units
}
/**
 * Build a stamp from its spec. EVERY type has one — see types/torch.ts for why even a type with no
 * coarse model at all is written as a spec rather than as a case in a switch here.
 */
function createStamp(type, variant, rotation = 0, seed = 0, lod = 0) {
    const spec = stampSpec(type);
    if (!spec)
        throw new Error(`Unknown stamp type: ${type}`);
    const p = spec.params(variant, seed);
    if (lod === 0) {
        const voxels = spec.lod0(p, variant, seed, rotation);
        return { type, variant, voxels, bounds: calculateBounds(voxels) };
    }
    if (lod <= (spec.foldLod ?? 0)) {
        // The FOLD levels draw the real model: a building hollow, with its doors, windows and
        // crenellations carved and its decorations in place; a rock with its grit and its squash. Their
        // proportions are LOD 0's by construction rather than by a stand-in's arithmetic.
        //
        // What it COSTS depends entirely on whether the model's identity includes an instance. A
        // building's does (yaw + seed), so its fold is regenerated per instance — the honest price of a
        // near ring that looks like the near view. A rock's does not, so all the rock folds in the world
        // amount to a couple of dozen generations.
        const voxels = foldVoxels(spec.lod0(p, variant, seed, rotation), 1 << lod);
        return { type, variant, voxels, bounds: calculateBounds(voxels) };
    }
    // maxLod is enforced HERE rather than left to each lodParts: a ladder that ends in a pin has no
    // natural stopping point of its own (a pin is meaningful at any stride), so every capped type would
    // have to remember to return null past its own cap. Buildings are capped at MAX_SCATTER_LOD, and
    // before this they kept drawing a one-voxel dot at lod 6+ — which is the per-column speckle the dot
    // deliberately excludes them from. A spec with no `lodParts` at all stops here too: it has no
    // stand-in, so above its fold levels there is nothing to draw.
    const parts = lod > spec.maxLod ? null : (spec.lodParts?.(p, lod) ?? null);
    if (!parts)
        return null;
    // The parts are in the stamp's own space; the placement yaw is applied by the rasterizer, so one
    // part list serves every rotation of the same building.
    const voxels = rasterizePartsAtScale(parts, 1 << lod, rotation);
    return { type, variant, voxels, bounds: calculateBounds(voxels) };
}
/** How many variants of each type the scatter may pick from. */
export function getVariantCount() {
    return VARIANTS_PER_TYPE;
}
export function getAllStampTypes() {
    return Object.values(StampType);
}
//# sourceMappingURL=registry.js.map